import { ButtonHTMLAttributes, ReactNode } from 'react';
import ButtonSpinner from './ButtonSpinner';

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  children: ReactNode;
  buttonType?: 'primary' | 'secondary' | 'text' | 'disable';
  size?: 'mid' | 'sm';
  isLoading?: boolean;
  loadingText?: string;
  /** min-widthを無効化し、親要素の幅に従う */
  fluid?: boolean;
}

// Base styles (common to all variants)
const BASE_STYLES =
  'font-noto-sans-jp font-bold leading-[1.4] rounded-lg overflow-hidden transition-colors flex items-center justify-center max-w-full';

// Min-width styles (separated for fluid prop control)
const MIN_WIDTH_STYLES = {
  mid: 'min-w-[310px] md:min-w-80',
  sm: 'min-w-[310px] md:min-w-[160px]',
} as const;

// Type-specific styles (without min-width, which is applied separately)
// SP: mid=310x46, sm=310x46 / PC: mid=320x46, sm=160x36
const TYPE_STYLES = {
  primary: {
    mid: 'bg-primary text-white hover:bg-primary-700 px-6 py-3 h-[46px] text-base text-nowrap',
    sm: 'bg-primary text-white hover:bg-primary-700 px-6 py-3 h-[46px] md:h-9 text-base md:text-sm md:py-2 text-center',
  },
  secondary: {
    mid: 'border border-mono-80 border-solid text-mono-80 hover:opacity-80 px-6 py-3 h-[46px] text-base text-nowrap',
    sm: 'border border-mono-80 border-solid text-mono-80 hover:opacity-80 px-6 py-3 h-[46px] md:h-9 text-base md:text-sm md:py-2 text-center',
  },
  text: {
    mid: 'text-primary hover:bg-gray-50 px-6 py-3 h-[46px] text-base text-nowrap',
    sm: 'text-primary hover:bg-gray-50 px-6 py-3 h-[46px] md:h-9 text-base md:text-sm md:py-2 text-center',
  },
  disable: {
    mid: 'bg-mono-30 text-white cursor-not-allowed px-6 py-3 h-[46px] text-base text-nowrap',
    sm: 'bg-mono-30 text-white cursor-not-allowed px-6 py-3 h-[46px] md:h-9 text-base md:text-sm md:py-2 text-center',
  },
} as const;

const Button = ({
  children,
  buttonType = 'primary',
  size = 'mid',
  className = '',
  disabled,
  isLoading = false,
  loadingText,
  fluid = false,
  type = 'button',
  ...props
}: ButtonProps) => {
  // Disable type should also set the disabled attribute
  const isDisabled = disabled || isLoading || buttonType === 'disable';

  // Get the appropriate styles based on buttonType and size
  // isLoading keeps the original buttonType style (e.g. primary stays blue with spinner)
  // Only explicit disabled or buttonType='disable' switches to grey disabled style
  const effectiveButtonType = (disabled || buttonType === 'disable') ? 'disable' : buttonType;
  const currentStyles = TYPE_STYLES[effectiveButtonType][size];
  const minWidthStyles = fluid ? '' : MIN_WIDTH_STYLES[size];

  return (
    <button
      type={type}
      className={`${BASE_STYLES} ${currentStyles} ${minWidthStyles} ${className}`}
      disabled={isDisabled}
      {...props}
    >
      {isLoading ? (
        <span className="inline-flex items-center gap-2">
          <ButtonSpinner size={size === 'sm' ? 'sm' : 'md'} />
          {loadingText ?? children}
        </span>
      ) : (
        children
      )}
    </button>
  );
};

export default Button;
